import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users,
  Search,
  Plus,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Loader2,
  AlertCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  Building2,
  MoreVertical,
  KeyRound
} from 'lucide-react'
import { adminApi, AdminUser, AdminOrganization, CreateUserRequest, UpdateUserRequest } from '@/api/admin'
import { rolesApi } from '@/api/roles'
import type { UserType, Gender, AdminRole } from '@/types/api'
import { useAuthStore } from '@/store/authStore'

const USER_TYPES: UserType[] = ['individual', 'parent', 'student', 'employee', 'teacher', 'admin']
const GENDERS: Gender[] = ['male', 'female', 'other', 'prefer_not_to_say']

const USER_TYPE_LABELS: Record<UserType, string> = {
  individual: '개인 (B2C)',
  parent: '학부모',
  student: '학생',
  employee: '기업 (B2B)',
  teacher: 'Learning Center',
  admin: '깨봉 Admin'
}

interface UserModalProps {
  user?: AdminUser | null
  onClose: () => void
  onSave: (data: CreateUserRequest | UpdateUserRequest) => void
  isLoading: boolean
  roles?: AdminRole[]
  onAssignRole?: (userId: number, roleId: number | null) => void
}

function UserModal({ user, onClose, onSave, isLoading, roles, onAssignRole }: UserModalProps) {
  const [formData, setFormData] = useState<CreateUserRequest>({
    email: user?.email || '',
    password: '',
    userType: user?.userType || 'individual',
    fullName: user?.fullName || '',
    displayName: user?.displayName || '',
    phoneNumber: user?.phoneNumber || '',
    isActive: user?.isActive ?? true,
    isEmailVerified: user?.isEmailVerified ?? false
  })
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(user?.adminRoleId ?? null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (user) {
      const updateData: UpdateUserRequest = { ...formData }
      delete (updateData as Record<string, unknown>).password
      onSave(updateData)
      // 역할이 변경된 경우 역할 배정 API 호출
      if (onAssignRole && selectedRoleId !== (user.adminRoleId ?? null)) {
        onAssignRole(user.id, selectedRoleId)
      }
    } else {
      // 비밀번호가 비어있으면 필드를 제외 (소셜 로그인 사용자)
      const createData = { ...formData }
      if (!createData.password || createData.password.trim() === '') {
        delete (createData as Record<string, unknown>).password
      }
      onSave(createData)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {user ? '사용자 수정' : '새 사용자 생성'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="label">이메일 *</label>
            <input
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          {!user && (
            <div>
              <label className="label">비밀번호</label>
              <input
                type="password"
                className="input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="소셜 로그인 사용자는 비워두세요"
              />
            </div>
          )}

          <div>
            <label className="label">사용자 유형 *</label>
            <select
              className="input"
              value={formData.userType}
              onChange={(e) => setFormData({ ...formData, userType: e.target.value as UserType })}
              required
            >
              {USER_TYPES.map((type) => (
                <option key={type} value={type}>{USER_TYPE_LABELS[type]}</option>
              ))}
            </select>
          </div>

          {formData.userType === 'admin' && user && roles && roles.length > 0 && (
            <div>
              <label className="label">역할</label>
              <select
                className="input"
                value={selectedRoleId ?? ''}
                onChange={(e) => setSelectedRoleId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">역할 없음</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="label">이름 *</label>
            <input
              type="text"
              className="input"
              value={formData.fullName}
              onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">표시 이름</label>
            <input
              type="text"
              className="input"
              value={formData.displayName || ''}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
            />
          </div>

          <div>
            <label className="label">전화번호</label>
            <input
              type="tel"
              className="input"
              value={formData.phoneNumber || ''}
              onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">활성 상태</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isEmailVerified}
                onChange={(e) => setFormData({ ...formData, isEmailVerified: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">이메일 인증됨</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              취소
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary btn-md flex-1">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : user ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

interface ChangePasswordModalProps {
  user: AdminUser
  onClose: () => void
  onSave: (newPassword: string) => void
  isLoading: boolean
}

function ChangePasswordModal({ user, onClose, onSave, isLoading }: ChangePasswordModalProps) {
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (newPassword.length < 8) {
      setError('비밀번호는 최소 8자 이상이어야 합니다.')
      return
    }
    if (newPassword !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.')
      return
    }
    setError('')
    onSave(newPassword)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">비밀번호 변경</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            <span className="font-medium text-slate-900">{user.email}</span> 사용자의 비밀번호를 변경합니다.
          </p>

          <div>
            <label className="label">새 비밀번호 *</label>
            <input
              type="password"
              className="input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="8자 이상 입력"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="label">비밀번호 확인 *</label>
            <input
              type="password"
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="비밀번호 재입력"
              required
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              취소
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary btn-md flex-1">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : '변경'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

function ActionMenu({ user, onEdit, onToggle, onChangePassword, onDelete }: {
  user: AdminUser
  onEdit: () => void
  onToggle: () => void
  onChangePassword: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const menuHeight = 180
      const dropUp = rect.bottom + menuHeight > window.innerHeight
      setMenuPos({
        top: dropUp ? rect.top - menuHeight : rect.bottom + 4,
        left: Math.max(8, rect.right - 176),
      })
    }
    setOpen(!open)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="p-2 hover:bg-slate-100 rounded-lg text-slate-500"
      >
        <MoreVertical className="w-5 h-5" />
      </button>

      {open && menuPos && (
        <div
          className="fixed z-50 w-44 bg-white rounded-lg shadow-lg border border-slate-200 py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <button
            onClick={() => { onToggle(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {user.isActive
              ? <ToggleRight className="w-4 h-4 text-green-600" />
              : <ToggleLeft className="w-4 h-4" />}
            {user.isActive ? '비활성화' : '활성화'}
          </button>
          <button
            onClick={() => { onEdit(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Edit2 className="w-4 h-4" />
            정보 수정
          </button>
          <button
            onClick={() => { onChangePassword(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <KeyRound className="w-4 h-4" />
            비밀번호 변경
          </button>
          <div className="my-1 border-t border-slate-100" />
          <button
            onClick={() => { onDelete(); setOpen(false) }}
            className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            삭제
          </button>
        </div>
      )}
    </div>
  )
}

export function UserManagement() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [searchKeyword, setSearchKeyword] = useState('')
  const [filterType, setFilterType] = useState<UserType | ''>('')
  const [filterOrganization, setFilterOrganization] = useState<number | ''>('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [modalUser, setModalUser] = useState<AdminUser | null | undefined>(undefined)
  const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null)
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<AdminUser | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // 조직 목록 조회
  const { data: organizations } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => adminApi.getOrganizations(),
    staleTime: 5 * 60 * 1000,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'users', { page, keyword: searchKeyword, filterType, filterOrganization, filterActive }],
    queryFn: () => adminApi.getUsers({
      page,
      size: 20,
      keyword: searchKeyword || undefined,
      userType: filterType || undefined,
      organizationId: filterOrganization || undefined,
      isActive: filterActive === '' ? undefined : filterActive
    }),
    staleTime: 30 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: adminApi.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setModalUser(undefined)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      adminApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setModalUser(undefined)
    }
  })

  const toggleMutation = useMutation({
    mutationFn: adminApi.toggleUserStatus,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
  })

  const deleteMutation = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setDeleteConfirmUser(null)
      setDeleteError(null)
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || '사용자 삭제에 실패했습니다.'
      setDeleteError(message)
    }
  })

  const changePasswordMutation = useMutation({
    mutationFn: ({ id, newPassword }: { id: number; newPassword: string }) =>
      adminApi.changeUserPassword(id, newPassword),
    onSuccess: () => {
      setPasswordUser(null)
      alert('비밀번호가 변경되었습니다.')
    }
  })

  const isSuperAdmin = useAuthStore((s) => s.isSuper())

  // 역할 목록 조회 (Super Admin만)
  const { data: rolesData } = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: async () => {
      const res = await rolesApi.getRoles()
      return res.data.data
    },
    enabled: isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  })

  const assignRoleMutation = useMutation({
    mutationFn: ({ userId, roleId }: { userId: number; roleId: number | null }) =>
      rolesApi.assignUserRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    }
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchKeyword(keyword)
    setPage(0)
  }

  const handleSave = (formData: CreateUserRequest | UpdateUserRequest) => {
    if (modalUser) {
      updateMutation.mutate({ id: modalUser.id, data: formData as UpdateUserRequest })
    } else {
      createMutation.mutate(formData as CreateUserRequest)
    }
  }

  const handleDelete = (user: AdminUser) => {
    setDeleteError(null)
    setDeleteConfirmUser(user)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">사용자 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-blue-600" />
            사용자 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {data?.totalElements ?? 0}명의 사용자</p>
        </div>
        <button
          onClick={() => setModalUser(null)}
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          새 사용자
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="이메일, 이름 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-40"
            value={filterType}
            onChange={(e) => { 
              setFilterType(e.target.value as UserType | ''); 
              // 직원이 아닌 유형을 선택하면 조직 필터 초기화
              if (e.target.value !== 'employee') {
                setFilterOrganization('');
              }
              setPage(0);
            }}
          >
            <option value="">모든 유형</option>
            {USER_TYPES.map((type) => (
              <option key={type} value={type}>{USER_TYPE_LABELS[type]}</option>
            ))}
          </select>
          {filterType === 'employee' && (
            <select
              className="input w-48"
              value={filterOrganization}
              onChange={(e) => { 
                setFilterOrganization(e.target.value ? Number(e.target.value) : ''); 
                setPage(0);
              }}
            >
              <option value="">모든 회사</option>
              {organizations?.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.displayName || org.name}
                </option>
              ))}
            </select>
          )}
          <select
            className="input w-32"
            value={filterActive === '' ? '' : filterActive.toString()}
            onChange={(e) => {
              setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
              setPage(0)
            }}
          >
            <option value="">모든 상태</option>
            <option value="true">활성</option>
            <option value="false">비활성</option>
          </select>
          <button type="submit" className="btn-secondary btn-md">
            <Filter className="w-4 h-4" />
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-visible">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700 rounded-tl-xl">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">이름</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">이메일</th>
                <th className="text-left pl-6 pr-2 py-4 text-sm font-semibold text-slate-700">유형</th>
                <th className="text-left px-4 py-4 text-sm font-semibold text-slate-700">역할</th>
                <th className="text-left pl-2 pr-6 py-4 text-sm font-semibold text-slate-700">상태</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">가입일</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700 rounded-tr-xl">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.content.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">{user.id}</td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">{user.fullName}</td>
                  <td className="px-6 py-4 text-sm text-slate-700">{user.email}</td>
                  <td className="pl-6 pr-2 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium
                      ${user.userType === 'admin' ? 'bg-red-100 text-red-700' :
                        user.userType === 'teacher' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'}`}>
                      {USER_TYPE_LABELS[user.userType]}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {user.userType === 'admin' && user.adminRoleName ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        {user.adminRoleName}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">-</span>
                    )}
                  </td>
                  <td className="pl-2 pr-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium
                      ${user.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                      {user.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {new Date(user.createdAt).toLocaleDateString('ko-KR')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <ActionMenu
                      user={user}
                      onEdit={() => setModalUser(user)}
                      onToggle={() => toggleMutation.mutate(user.id)}
                      onChangePassword={() => setPasswordUser(user)}
                      onDelete={() => handleDelete(user)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              {data.page * data.size + 1} - {Math.min((data.page + 1) * data.size, data.totalElements)} / {data.totalElements}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={data.first}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm text-slate-600">
                {data.page + 1} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={data.last}
                className="p-2 hover:bg-slate-100 rounded-lg disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalUser !== undefined && (
        <UserModal
          user={modalUser}
          onClose={() => setModalUser(undefined)}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
          roles={isSuperAdmin ? rolesData : undefined}
          onAssignRole={isSuperAdmin ? (userId, roleId) =>
            assignRoleMutation.mutate({ userId, roleId })
          : undefined}
        />
      )}

      {passwordUser && (
        <ChangePasswordModal
          user={passwordUser}
          onClose={() => setPasswordUser(null)}
          onSave={(newPassword) =>
            changePasswordMutation.mutate({ id: passwordUser.id, newPassword })
          }
          isLoading={changePasswordMutation.isPending}
        />
      )}

      {deleteConfirmUser && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setDeleteConfirmUser(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">사용자 삭제 확인</h3>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-[4rem_1fr] gap-y-1.5 text-sm">
                  <span className="text-slate-500">이름</span>
                  <span className="font-medium text-slate-900">{deleteConfirmUser.fullName}</span>
                  <span className="text-slate-500">이메일</span>
                  <span className="text-slate-700">{deleteConfirmUser.email}</span>
                  <span className="text-slate-500">유형</span>
                  <span className="text-slate-700">{USER_TYPE_LABELS[deleteConfirmUser.userType]}</span>
                  <span className="text-slate-500">ID</span>
                  <span className="text-slate-700">{deleteConfirmUser.id}</span>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                <p className="text-sm text-amber-800">
                  <strong>주의:</strong> 이 사용자가 기관에 소속되어 있거나, 주문/구독 내역이 있는 경우 삭제가 실패할 수 있습니다.
                  삭제된 데이터는 복구할 수 없습니다.
                </p>
              </div>

              {deleteError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-4">
                  <p className="text-sm text-red-700 font-medium">삭제할 수 없습니다</p>
                  <p className="text-sm text-red-600 mt-1">{deleteError}</p>
                </div>
              ) : (
                <p className="text-sm text-slate-700 mt-4 font-medium">정말로 이 사용자를 삭제하시겠습니까?</p>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-200">
              <button
                onClick={() => { setDeleteConfirmUser(null); setDeleteError(null) }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                disabled={deleteMutation.isPending}
              >
                {deleteError ? '닫기' : '취소'}
              </button>
              {!deleteError && (
                <button
                  onClick={() => deleteMutation.mutate(deleteConfirmUser.id)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {deleteMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  삭제
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}





















